// src/components/dashboard/Workshop5Charts.js
// Dashboard Atelier 5 — the risk-scenario table reuses the exact same data and
// component as "Récapitulatif des scénarios de risque" (Synthèse, Atelier 5).
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useAnalysisStore from '../../stores/useAnalysisStore';
import useAtelier5Store from '../../stores/useAtelier5Store';
import {
    RiskScenarioRecapTable,
    buildAttackPathScenarios,
    buildRiskScenarioIndexMap,
    resolveRiskLevelsConfig,
} from '../Atelier5/ActiviteA/RiskScenarioListView';

const Workshop5Charts = ({ data, rawStrategicScenarios = [] }) => {
    const { t } = useTranslation();
    void data;
    void rawStrategicScenarios;

    // Same source as Synthèse: synthesisData from the store (not backend aggregates,
    // not atelier4 operationalScenarios).
    const synthesisData = useAtelier5Store((s) => s.synthesisData);
    const currentAnalysis = useAnalysisStore((s) => s.currentAnalysis);

    const strategicScenarios = useMemo(() => synthesisData?.strategicScenarios || [], [synthesisData]);
    const operationalScenarios = useMemo(() => synthesisData?.operationalScenarios || [], [synthesisData]);
    const riskLevelsCount = currentAnalysis?.riskLevelsCount || 5;

    const riskLevelsConfig = useMemo(
        () => resolveRiskLevelsConfig(currentAnalysis?.riskLevels, riskLevelsCount),
        [currentAnalysis?.riskLevels, riskLevelsCount]
    );
    const attackPathScenarios = useMemo(
        () => buildAttackPathScenarios(strategicScenarios, operationalScenarios, riskLevelsCount),
        [strategicScenarios, operationalScenarios, riskLevelsCount]
    );
    const riskScenarioIndexMap = useMemo(
        () => buildRiskScenarioIndexMap(strategicScenarios),
        [strategicScenarios]
    );

    // Summary counts consistent with the table rows (risk scenarios, not strategic).
    const totalScenarios = attackPathScenarios.length;
    const scenariosWithTreatment = useMemo(() => {
        let count = 0;
        for (const ap of attackPathScenarios) {
            const ss = strategicScenarios.find((s) =>
                s.attackPaths?.some((a) => (typeof a === 'string' ? a : a.id) === ap.id)
            );
            if (ss?.attackPathTreatments?.[ap.id]?.option) count += 1;
        }
        return count;
    }, [attackPathScenarios, strategicScenarios]);

    if (!synthesisData) {
        return (
            <div className="bg-white p-6 rounded-lg shadow-sm border">
                <p className="text-gray-500">{t('workshop5Charts.noData')}</p>
            </div>
        );
    }

    return (
        <div className="space-y-8">
            {/* Enhanced Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="group bg-gradient-to-br from-blue-50 to-indigo-100 p-6 rounded-xl border border-blue-200 hover:shadow-lg hover:scale-105 transition-all duration-300 cursor-pointer">
                    <div className="flex items-center justify-between">
                        <div>
                            <h4 className="text-sm font-medium text-blue-800 mb-1">Total Scénarios</h4>
                            <p className="text-3xl font-bold text-blue-900 group-hover:text-blue-700 transition-colors">
                                {totalScenarios}
                            </p>
                            <p className="text-xs text-blue-600 mt-1">{t('workshop5Charts.scenariosAnalyzed')}</p>
                        </div>
                        <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center group-hover:bg-blue-600 transition-colors shadow-lg">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                        </div>
                    </div>
                </div>

                <div className="group bg-gradient-to-br from-purple-50 to-violet-100 p-6 rounded-xl border border-purple-200 hover:shadow-lg hover:scale-105 transition-all duration-300 cursor-pointer">
                    <div className="flex items-center justify-between">
                        <div>
                            <h4 className="text-sm font-medium text-purple-800 mb-1">{t('workshop5Charts.withTreatment')}</h4>
                            <p className="text-3xl font-bold text-purple-900 group-hover:text-purple-700 transition-colors">
                                {scenariosWithTreatment}
                            </p>
                            <p className="text-xs text-purple-600 mt-1">
                                {totalScenarios > 0 ? t('workshop5Charts.treatedPercentage', { percentage: ((scenariosWithTreatment / totalScenarios) * 100).toFixed(1) }) : t('workshop5Charts.treatedPercentageZero')}
                            </p>
                        </div>
                        <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center group-hover:bg-purple-600 transition-colors shadow-lg">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                    </div>
                </div>
            </div>

            {/* Risk scenarios — same rows as Récapitulatif des scénarios de risque */}
            {attackPathScenarios.length > 0 ? (
                <RiskScenarioRecapTable
                    strategicScenarios={strategicScenarios}
                    attackPathScenarios={attackPathScenarios}
                    riskScenarioIndexMap={riskScenarioIndexMap}
                    riskLevels={riskLevelsConfig}
                />
            ) : (
                <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100">
                    <div className="text-center py-12">
                        <div className="text-gray-400 mb-4">
                            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                            </svg>
                        </div>
                        <p className="text-gray-500 text-lg">{t('workshop5Charts.noScenarioData')}</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Workshop5Charts;
